import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import useDialogA11y from "@/lib/useDialogA11y";
import api, { errMsg } from "@/lib/api";

// Short, skippable outcome question when a change closes, and once more ~90 days after completion. Free text stays in the tenant.
export default function OutcomeDialog({ change, trigger, onDone }) {
  const [st, setSt] = useState(null);
  const [pick, setPick] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const stage = st?.due_close ? "close" : st?.due_day90 ? "day90" : null;
  useEffect(() => {
    if (!["completed", "cancelled"].includes(change.status) || change.is_example) { setSt(null); return; }
    api.get(`/changes/${change.id}/outcome`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [change.id, change.status, trigger]); // eslint-disable-line
  const open = !!stage;
  const close = () => setSt(null);
  const dialogRef = useDialogA11y(open, close);
  if (!open) return null;
  const send = async (outcome) => {
    setBusy(true);
    try { await api.post(`/changes/${change.id}/outcome`, { stage, outcome, note: outcome ? note : "" }); if (outcome) toast.success("Outcome recorded — thank you, this is what makes the benchmarks honest."); setSt(null); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4" data-testid="outcome-dialog">
      <div aria-hidden="true" className="absolute inset-0 bg-ink/50" onClick={() => send(null)} />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="outcome-title" className="relative w-full max-w-lg pp-card p-6 shadow-2xl">
        <button type="button" aria-label="Skip for now" data-testid="outcome-skip-x" onClick={() => send(null)} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"><X size={16} /></button>
        <div className="text-[11px] font-bold uppercase tracking-widest text-accent-700">{stage === "close" ? "One quick question" : "90 days on"}</div>
        <h2 id="outcome-title" className="font-head text-xl font-extrabold mt-1">{stage === "close" ? `How did ${change.ref} actually land?` : `Is ${change.ref} still holding, three months on?`}</h2>
        <p className="text-sm text-subtle mt-1">Optional. Only the option you pick is contributed to the anonymous benchmark pool — your note stays here.</p>
        <div className="mt-4 space-y-2" role="radiogroup" aria-label="Outcome">
          {Object.entries(st.options).map(([k, label]) => (
            <label key={k} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm cursor-pointer transition-colors ${pick === k ? "border-ink bg-muted" : "border-border hover:border-accent-400"}`}>
              <input type="radio" name="outcome" value={k} checked={pick === k} onChange={() => setPick(k)} data-testid={`outcome-${k}`} className="h-4 w-4" /><span className="font-semibold text-foreground">{label}</span>
            </label>
          ))}
        </div>
        <textarea data-testid="outcome-note" aria-label="Anything worth remembering (kept private to your organisation)" className="pp-input mt-3 min-h-[70px] text-sm" placeholder="Anything worth remembering? Kept private to your organisation." value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="flex items-center justify-between mt-4">
          <button type="button" data-testid="outcome-skip" onClick={() => send(null)} disabled={busy} className="text-xs font-bold text-muted-foreground hover:text-foreground">Skip — don't ask again{stage === "close" ? " until 90 days" : ""}</button>
          <button type="button" data-testid="outcome-save" onClick={() => send(pick)} disabled={busy || !pick} className="pp-btn-dark text-sm">Record outcome</button>
        </div>
      </div>
    </div>
  );
}
